/**
 * Brand Gorse engagement events, separate from catalog list helpers.
 * Fire-and-forget POST /api/brands/events/
 */

import { getApiBaseUrl } from "@/lib/api/config";

const VISITOR_KEY = "rivyou_visitor_id";

/**
 * Stable anonymous id shared with Mixpanel (web_<uuid>).
 */
export function getBrandEventUserId() {
  if (typeof window === "undefined") return null;
  try {
    let id = window.localStorage.getItem(VISITOR_KEY);
    if (!id) {
      const uuid =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      id = `web_${uuid}`;
      window.localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

/**
 * @param {object} payload
 * @param {string} payload.event - view | click | card_click | website_click | search_select | request_audit
 * @param {string} payload.brandId
 * @param {string} [payload.source]
 * @param {string} [payload.query]
 */
export async function trackBrandEvent({ event, brandId, source = "", query = "" }) {
  if (!event || !brandId) return { ok: false };

  const body = {
    event,
    brand_id: brandId,
    user_id: getBrandEventUserId() || "",
    source: source || "",
    query: query || "",
  };

  try {
    const res = await fetch(`${getApiBaseUrl()}/api/brands/events/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
      keepalive: true,
    });
    if (!res.ok) return { ok: false, status: res.status };
    const data = await res.json().catch(() => ({}));
    return { ok: true, ...data };
  } catch {
    return { ok: false };
  }
}

/** Brand detail opened */
export function trackBrandView(brandId, source = "detail") {
  return trackBrandEvent({ event: "view", brandId, source });
}

/** Card / row navigated to detail */
export function trackBrandCardClick(brandId, source = "card") {
  return trackBrandEvent({ event: "card_click", brandId, source });
}

/** External visit website */
export function trackBrandWebsiteClick(brandId, source = "detail") {
  return trackBrandEvent({ event: "website_click", brandId, source });
}

/** Search suggestion selected */
export function trackBrandSearchSelect(brandId, query = "", source = "search") {
  return trackBrandEvent({
    event: "search_select",
    brandId,
    source,
    query,
  });
}

/** Audit request submitted */
export function trackBrandAuditRequest(brandId, source = "modal") {
  return trackBrandEvent({ event: "request_audit", brandId, source });
}

/** `source` of a first page that came from the plain list, not /trending/. */
export const LIST_FALLBACK_SOURCE = "list-fallback";

/**
 * GET /api/brands/trending/: Gorse-ranked brands (falls back server-side).
 */
export async function getTrendingBrands(params = {}) {
  const { getBrands, normalizeListPayload } = await import("@/lib/api/brands");
  const { limit, ...rest } = params;
  const merged = { page_size: limit ?? 48, ...rest };
  const query = new URLSearchParams();
  Object.entries(merged).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== "") {
      query.set(key, String(val));
    }
  });
  const qs = query.toString() ? `?${query.toString()}` : "";

  // A cursor is bound to the trending query, so only the first page can
  // fall back to the plain list. That page's cursor belongs to the list
  // endpoint, so it is tagged to keep later pages off /trending/.
  const fallback = async () =>
    merged.cursor
      ? normalizeListPayload({})
      : { ...(await getBrands({ ...merged, sort: "popular" })), source: LIST_FALLBACK_SOURCE };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);

  try {
    const res = await fetch(`${getApiBaseUrl()}/api/brands/trending/${qs}`, {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    if (!res.ok) {
      return fallback();
    }
    const page = normalizeListPayload(await res.json());
    return { ...page, source: page.source || "gorse" };
  } catch {
    clearTimeout(timeoutId);
    return fallback();
  }
}
